import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  NO_ERRORS_SCHEMA,
  OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PricingDetail } from 'src/app/api/api-checkout/models/rq-checkout-search';
import { FareBreakDown, IUpSell } from 'src/app/api/api-checkout/models/rq-checkout-up-sell';
import { GlobalComponent } from 'src/app/shared/global';
import { CommonModule } from '@angular/common';
import { NguCarouselConfig, NguCarouselModule } from '@ngu/carousel';
import { UpsellService } from '../../../../services/upsell/upsell.service';
import { ConfigCatService } from '../../../../services/labs';
import { UpsellCardComponent } from './components/upsell-card/upsell-card.component';
import { FlightDataService } from '../../../../public/booking/services';
import { catchError, finalize, take } from 'rxjs/operators';
import { of } from 'rxjs';

@Component({
  selector: 'app-upsell-selector-modal',
  templateUrl: './upsell-selector-modal.component.html',
  styleUrls: ['./upsell-selector-modal.component.scss'],
  standalone: true,
  imports: [CommonModule, NguCarouselModule, UpsellCardComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA],
})
export class UpsellSelectorModalComponent implements OnInit, AfterViewInit {
  @Input() trackingCode: string;

  private itinerary = this.flightDataService.getValue()!;

  dataUpSell: IUpSell[] = [];

  isDomestic = this.itinerary.isDomestic;
  pricingDetail: PricingDetail =
    this.itinerary.flight.detailPricing ?? GlobalComponent.detailPricing;

  carouselConfig: NguCarouselConfig = {
    easing: 'cubic-bezier(0, 0, 0.2, 1)',
    grid: { xs: 1, sm: 1, md: 2, lg: 3, xl: 3, all: 0 },
    speed: 800,
    slide: 1,
    point: { visible: false },
    touch: true,
    animation: 'lazy',
    loop: false,
  };
  normalizing = true;

  constructor(
    private readonly router: Router,
    private readonly upsellService: UpsellService,
    private readonly flightDataService: FlightDataService,
    private readonly configCatService: ConfigCatService,
    private readonly cdr: ChangeDetectorRef,
    public readonly activeModal: NgbActiveModal
  ) {}

  ngOnInit(): void {
    this.processUpsellData();
  }

  ngAfterViewInit() {
    this.cdr.detectChanges();
  }

  private processUpsellData() {
    const rawUpsells = this.upsellService.getUpsellListValue();
    this.setUpsellData(rawUpsells);
    this.normalizeUpsellsFeatures(rawUpsells);
  }

  private normalizeUpsellsFeatures(rawUpsells: IUpSell[]) {
    this.normalizing = true;

    const optimizedUpsells = rawUpsells.map(
      ({ index, name, informationServices, ...rest }): IUpSell => ({
        index,
        name,
        ...rest,
        informationServices: (informationServices ?? []).filter((info) => info.itsInclude),
      })
    );

    this.upsellService
      .normalizeUpsellDescriptions(optimizedUpsells, this.itinerary)
      .pipe(
        take(1),
        finalize(() => (this.normalizing = false)),
        catchError((error) => {
          console.warn('Error al normalizar upsells', error);
          return of(null);
        })
      )
      .subscribe((response) => {
        if (!response?.state?.ok || !response.result) return;

        const normalized = rawUpsells.map((upsell) => {
          const result = response.result.find(
            (r) => r.name === upsell.name && r.index === upsell.index
          );
          if (!result) return upsell;

          const updatedServices = upsell.informationServices?.map((service) => {
            const match = result.informationServices.find(
              (r) => r.original === service.description
            );
            return match ? { ...service, description: match.description } : service;
          });

          return { ...upsell, informationServices: updatedServices };
        });

        this.setUpsellData(normalized);
      });
  }

  private setUpsellData(upsells: IUpSell[]) {
    const mapped = upsells.map((item, index) => this.mapUpsellItem(item, index));
    this.dataUpSell = this.sortUpsellByPrice(mapped);
  }

  private mapUpsellItem(item: IUpSell, index: number): IUpSell {
    item.index = index;
    item.totalPay = this.calculateTotalPrice(item.fareBreakDowns);
    item.dataBags = [];
    item.dataExtras = [];

    item.informationServices?.forEach((service) => {
      service.isBag = this.isBag(service.description);
      if (service.itsInclude) (service.isBag ? item.dataBags : item.dataExtras)?.push(service);
    });

    return item;
  }

  private sortUpsellByPrice(items: IUpSell[]): IUpSell[] {
    return items.sort((a, b) => {
      if (!a.totalPay) return 1;
      if (!b.totalPay) return -1;
      return a.totalPay - b.totalPay;
    });
  }

  private calculateTotalPrice(fareBreakDowns: FareBreakDown[]): number {
    const feeNMV = this.pricingDetail.feeNMV ?? 0;
    const feePTA = this.pricingDetail.feePTA ?? 0;

    return (
      fareBreakDowns.reduce((acc, fb) => {
        const baseFare: number = fb.baseFare * fb.quantity;
        const taxes = fb.taxes.reduce((sum, t) => sum + t.fareAmount, 0) * fb.quantity;
        return acc + baseFare + taxes;
      }, 0) +
      feeNMV +
      feePTA
    );
  }

  private isBag(description: string | undefined): boolean {
    const desc = description?.toLocaleLowerCase() ?? '';
    return ['bag', 'baggage', 'equipaje', 'mano', 'carry', 'upto', 'bodega'].some((word) =>
      desc.includes(word)
    );
  }

  clickSelectUpSell(item: IUpSell) {
    this.upsellService.setSelectedUpsell(item);

    if (this.router.url.includes('resultados')) {
      GlobalComponent.appBooking.brandedFareName = item.name;
      this.router.navigateByUrl(this.configCatService.bookingUrl).then();
    }

    this.activeModal.close(item);
  }
}
